import { Box, Typography, Grid, Paper, Button, IconButton, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Badge } from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import FilterListIcon from '@mui/icons-material/FilterList';
import { AccountBalanceWallet, AddCard, AlignVerticalTop, Delete, Search } from '@mui/icons-material';
import { ColumnTitle, Pagination, ScrollbarCustom, WrapTable } from '../../../../components';
import { IUserDetail } from '../../../../types/User';
import { useGetTokenizedCardList } from '../../../../services/tokenized-card/useTokenizedCard';
import { ITokenizedCard } from '../../../../types/TokenizedCard';
import { useState } from 'react';
import { formatPrice, formatPriceWithCommas } from '../../../../utils/format';
import { convertToDateTime } from '../../../../utils/datetime';
import TopUpModal from '../../../../components/Modal/UserDetail/ModalTopUp';
import { useCurrentProfile } from '../../../../hooks/CommonContext';

export const PaymentMethod = ({ userDetail }: { userDetail: IUserDetail }) => {
    const adminProfile = useCurrentProfile();
    const [page, setPage] = useState(1);

    const { data: tokenizedCardList, isLoading, isFetching } = useGetTokenizedCardList(userDetail._id!, page, { per_page: 10 });
    const { data: tableData, total, per_page } = tokenizedCardList || { data: [], total: 0, per_page: 10 };
    const [openTopUpModal, setOpenTopUpModal] = useState(false);

    return (
        <>
            <Box sx={{ color: 'white', p: 4 }}>
                <Stack direction="row" alignItems="center" mb={4} spacing={2}>
                    <Box sx={{ mr: 2, display: 'flex', alignItems: 'center' }}>
                        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', fontSize: 32, mr: 1 }}>
                            <AccountBalanceWallet style={{ fill: '#FFD600', width: 32, height: 32 }} />
                        </Box>
                        <Box>
                            <Typography variant="h5" fontWeight={600}>Payment Method</Typography>
                            <Typography variant="body2" color="gray">Get a detailed breakdown of user payment methods.</Typography>
                        </Box>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Stack direction="row" spacing={1}>
                        {adminProfile?.role === 'Superadmin' && (<Button variant="outlined" startIcon={<AlignVerticalTop />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }} onClick={() => {
                            setOpenTopUpModal(true);
                        }}>
                            Top up
                        </Button>)}
                        <Button variant="outlined" startIcon={<AddCard />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                            Add bank card
                        </Button>
                        <Button variant="outlined" startIcon={<Search />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                            Search
                        </Button>
                        <Button variant="outlined" startIcon={<DownloadIcon />} sx={{ borderRadius: 5, color: 'white', borderColor: '#0D0D0D', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                            Download CSV
                        </Button>
                        <IconButton sx={{ borderRadius: 3, color: 'white', border: '1px solid #444', bgcolor: '#0D0D0D', '&:hover': { bgcolor: '#333', borderColor: '#666' } }}>
                            <FilterListIcon />
                        </IconButton>
                    </Stack>
                </Stack>
                <Paper elevation={0} sx={{ bgcolor: '#191919', borderRadius: 4, p: 4 }}>
                    <Box mb={2}>
                        <Grid container spacing={4} m={[0.5, 0.5]}>
                            <Grid item lg={3} md={6} sm={6} xs={12} display={'flex'} alignContent={'center'} sx={{ padding: '0!important' }}>
                                <Typography variant="h3" fontWeight={300} color={'#9B9B9B'}>Wattpay balance</Typography>
                            </Grid>
                            <Grid item lg={3} md={6} sm={6} xs={12} padding={0} sx={{ padding: '0!important' }}>
                                <Typography variant="h3" fontWeight={600}>{formatPriceWithCommas(userDetail.wallet_balance / 100)}</Typography>
                            </Grid>
                        </Grid>
                    </Box>
                    <Box mb={2}>
                        <WrapTable p={"5px 21px"} height="100%" marginBottom={3} isLoading={isLoading || isFetching}>
                            <TableContainer>
                                <ScrollbarCustom autoHide>
                                    <Table sx={{ minWidth: 800, marginBottom: "10px" }}>
                                        <TableHead>
                                            <TableRow>
                                                <ColumnTitle sx={{ pl: 0 }}>SN</ColumnTitle>
                                                {/* <ColumnTitle sx={{ minWidth: 150 }}>Bank Card</ColumnTitle> */}
                                                <ColumnTitle>Category</ColumnTitle>
                                                <ColumnTitle sx={{ minWidth: 150 }}>Card Number</ColumnTitle>
                                                <ColumnTitle sx={{ minWidth: 169 }}>Date Added</ColumnTitle>
                                                <ColumnTitle align='center'>Status</ColumnTitle>
                                                <ColumnTitle align='center'>Action</ColumnTitle>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {tableData.map((row: ITokenizedCard, index: number) => (
                                                <TableRow key={row._id}>
                                                    <TableCell sx={{ pl: 0 }}>{(page - 1) * per_page + index + 1}</TableCell>
                                                    {/* <TableCell>{row.name}</TableCell> */}
                                                    <TableCell>{row.brand}</TableCell>
                                                    <TableCell>**** **** {row.number}</TableCell>
                                                    <TableCell>{convertToDateTime(row.createdAt)}</TableCell>
                                                    <TableCell align='center'>
                                                        <Badge badgeContent={row.is_default ? 'Default' : 'Non-default'} color={row.is_default ? 'success' : 'warning'} sx={{ '.MuiBadge-badge': { width: '5rem' } }} />
                                                    </TableCell>
                                                    <TableCell align='center'>
                                                        <IconButton >
                                                            <Delete />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </ScrollbarCustom>
                            </TableContainer>
                        </WrapTable>
                        <Pagination
                            page={page}
                            setPage={setPage}
                            totalItems={total}
                            totalPage={Math.ceil(total / per_page)}
                            currentPage={page}
                        />
                    </Box>
                </Paper>
            </Box>
            <TopUpModal open={openTopUpModal} onClose={() => setOpenTopUpModal(false)} userId={userDetail._id} />
        </>
    );
}
